import type { CustomerData, CustomerFieldKey } from './types';

export type CustomerDataListener = (data: Readonly<CustomerData>) => void;

const FIELD_KEYS: CustomerFieldKey[] = [
  'email',
  'phone',
  'firstName',
  'lastName',
  'address1',
  'address2',
  'city',
  'state',
  'postalCode',
  'country',
];

function isValidValue(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Armazena e mescla dados do cliente capturados durante a sessão.
 * Singleton compartilhado entre AutoCustomerTracker, AutoCustomerAddToCart e AutoAddPaymentInfo.
 */
export class CustomerDataStore {
  private static instance: CustomerDataStore | null = null;
  private data: CustomerData = {};
  private listeners = new Set<CustomerDataListener>();

  static getInstance(): CustomerDataStore {
    if (!CustomerDataStore.instance) {
      CustomerDataStore.instance = new CustomerDataStore();
    }
    return CustomerDataStore.instance;
  }

  /** Apenas para testes. */
  static resetInstance(): void {
    CustomerDataStore.instance = null;
  }

  get(): Readonly<CustomerData> {
    return { ...this.data };
  }

  set(key: CustomerFieldKey, value: string): void {
    if (!isValidValue(value)) return;
    this.merge({ [key]: value.trim() });
  }

  merge(partial: Partial<CustomerData>): void {
    let changed = false;

    for (const key of FIELD_KEYS) {
      const value = partial[key];
      if (!isValidValue(value)) continue;
      const trimmed = value.trim();
      if (this.data[key] === trimmed) continue;
      this.data[key] = trimmed;
      changed = true;
    }

    if (changed) {
      this.notify();
    }
  }

  clear(): void {
    this.data = {};
    this.notify();
  }

  subscribe(listener: CustomerDataListener): () => void {
    this.listeners.add(listener);
    listener(this.get());
    return () => {
      this.listeners.delete(listener);
    };
  }

  hasRequiredForAddToCart(): boolean {
    return isValidValue(this.data.email) && isValidValue(this.data.phone);
  }

  hasRequiredForAddPaymentInfo(): boolean {
    return this.hasRequiredForAddToCart();
  }

  private notify(): void {
    const snapshot = this.get();
    this.listeners.forEach(listener => listener(snapshot));
  }
}
